import { useEffect, useMemo, useRef } from 'react';
import { Unsubscribe } from 'firebase/firestore';
import { useEmployeeStore } from '../store/employeeStore';
import { useAccountBookStore } from '../store/accountBookStore';
import { formatLocalDateString } from '../utils/dateUtils';
import { Timestamp as FirestoreTimestamp } from 'firebase/firestore';
import { onWorkSchedulesSnapshot } from '../services/firestoreService';

// 전역 플래그로 중복 fetch 방지 (가계부별로 관리)
let globalScheduleFetchInProgress = false;
const globalScheduleFetchedKeys = new Set<string>();

// 전역 캐시 초기화 함수 (외부에서 호출 가능)
export const clearWorkScheduleDataCache = () => {
  globalScheduleFetchedKeys.clear();
  globalScheduleFetchInProgress = false;
};

// Firestore Timestamp 혹은 문자열 날짜를 YYYY-MM-DD 형태로 정규화
const normalizeDateString = (dateValue: any): string | null => {
  try {
    if (!dateValue) return null;
    if (typeof dateValue === 'string') {
      return dateValue.split('T')[0];
    }
    if (
      typeof dateValue === 'object' &&
      'seconds' in dateValue &&
      'nanoseconds' in dateValue
    ) {
      const ts = dateValue as FirestoreTimestamp;
      const d = new Date(ts.seconds * 1000 + ts.nanoseconds / 1e6);
      return formatLocalDateString(d);
    }
    if (dateValue instanceof Date) {
      return formatLocalDateString(dateValue);
    }
    return null;
  } catch {
    return null;
  }
};

const toMonthKey = (d?: Date): string | null => {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * @param selectedDate 기준 월 (해당 월의 스케줄 구독)
 * @param secondaryDate 추가 월 — 주간 캘린더처럼 표시 범위가 두 달에 걸칠 때 인접 월 포함용
 */
export const useWorkScheduleData = (selectedDate?: Date, secondaryDate?: Date) => {
  const schedules = useEmployeeStore((state) => state.schedules);
  const schedulesLoading = useEmployeeStore((state) => state.schedulesLoading);
  const error = useEmployeeStore((state) => state.error);
  const fetchSchedules = useEmployeeStore((state) => state.fetchSchedules);
  const setSchedules = useEmployeeStore((state) => state.setSchedules);

  const userId = useAccountBookStore((state) => state.userId);
  const currentAccountNumber = useAccountBookStore((state) => state.currentAccountNumber);
  const currentAccountBook = useAccountBookStore((state) => state.currentAccountBook);
  const isInitialized = useAccountBookStore((state) => state.isInitialized);
  const storeLoading = useAccountBookStore((state) => state.isLoading);

  const accountNumber = useMemo(
    () => currentAccountNumber || currentAccountBook?.accountNumber,
    [currentAccountNumber, currentAccountBook?.accountNumber]
  );

  // selectedDate를 YYYY-MM 문자열로 정규화 — Date 객체 참조가 매 렌더마다 바뀌어도
  // 같은 월이면 같은 키가 나오므로 useEffect가 재실행되지 않는다.
  const monthKey = useMemo(() => toMonthKey(selectedDate), [selectedDate]);
  const secondaryMonthKey = useMemo(() => {
    const key = toMonthKey(secondaryDate);
    return key && key !== monthKey ? key : null;
  }, [secondaryDate, monthKey]);

  const isMountedRef = useRef(true);
  const prevAccountNumberRef = useRef<number | undefined>(undefined);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);

  // ⚠️ 과거엔 여기서 월-스코프 REST(getWorkSchedulesByUserFromShared, WHERE date IN [월초,월말])를
  // 별도로 한 번 더 받아왔다. 하지만 아래 실시간 리스너가 이미 전체 컬렉션을 구독해 monthKey로 필터하므로
  // REST는 순수 중복이었고, 게다가 (a) 3중 dedup(globalScheduleFetchInProgress/globalScheduleFetchedKeys/
  // store lastFetchKey)이 엉켜 특정 월 fetch를 영구 스킵시키고, (b) 이전 월의 늦게 도착한 REST 응답이
  // 리스너가 넣은 현재 월 데이터를 덮어써(현재 월 가드 없음) "월 바꿔도 비거나 이전 월이 남는" 버그를 냈다.
  // → REST 경로 제거, 리스너를 단일 소스로. (읽기량도 감소)

  // 실시간 리스너
  useEffect(() => {
    if (!isInitialized || !userId || !accountNumber) {
      return;
    }

    isMountedRef.current = true;

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    unsubscribeRef.current = onWorkSchedulesSnapshot(
      userId,
      (updatedSchedules) => {
        const filteredByAccount = updatedSchedules.filter(
          (s) => s.account_number === accountNumber || s.accountNumber === accountNumber
        );

        const finalSchedules = monthKey
          ? filteredByAccount.filter((s) => {
              const dateStr = normalizeDateString((s as any).date);
              if (dateStr === null) return false;
              return (
                dateStr.startsWith(monthKey) ||
                (secondaryMonthKey !== null && dateStr.startsWith(secondaryMonthKey))
              );
            })
          : filteredByAccount;

        const normalizedSchedules = finalSchedules.map((s) => {
          const normalizedDate = normalizeDateString((s as any).date);
          return {
            ...s,
            date: normalizedDate || (s as any).date,
          };
        });

        setSchedules(normalizedSchedules as any);
      },
      {
        orderByField: 'date',
        orderDirection: 'desc',
      }
    );

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [isInitialized, userId, accountNumber, monthKey, secondaryMonthKey, setSchedules]);

  // 언마운트 시 정리
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  return { schedules, loading: schedulesLoading, error };
};
